/**
 * The cells of the Settings › Warehouses table (ACT2-1031): the account's identity with its brand
 * icon, its status badge, and its one primary action. Row data comes from `warehouseList.ts`. An
 * account of another workspace (ACT2-1124) has the same cells: its Connect copies, then connects.
 */
import { Link2 } from 'lucide-react';
import * as React from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { cn } from '@/_shared/lib/utils';

import { accountActionName, adoptRefusal, coordinatesOf } from './adopt/adoptPresentation';
import type { AdoptAccountsHook, RsAccountOverview } from './adopt/useAdoptAccounts';
import { rudderstackActionBlocked } from './connectionRowActions';
import { refusalMessage } from './refusalCopy';
import {
  CANNOT_CONNECT_REASON,
  type OrientationIndicator,
  type OrientationState,
  WAREHOUSE_STATUS_LABEL,
  type WarehouseListRow,
  type WarehouseStatus,
} from './warehouseList';
import { useWarehouseListContext } from './warehouseListContext';
import { warehouseTypeMeta } from './warehouseTypes';

/**
 * What a `connection_exists` refusal says (ACT2-877 decision 1). Connect only ever CREATES, so the
 * refusal sends the admin to the connection already in this list: a working one moves onto this
 * account with Choose RudderStack account, a broken one needs Reconnect.
 */
function connectionExistsMessage(
  account: RsAccountOverview,
  existingName: string | undefined,
  existingPaused: boolean,
): string {
  const where = coordinatesOf(account) || 'That warehouse';
  const named = existingName === undefined || existingName === '' ? '' : ` as ${existingName}`;
  const remedy = existingPaused
    ? 'it needs attention — use “Reconnect” on it in this list'
    : 'to move it onto this account, use “Choose RudderStack account” (or “Connect”) on it in this list';
  return `${where} is already connected${named} — ${remedy}.`;
}

/** Why the last Connect on this account failed, or null. Only the row it was for renders it. */
function connectError(
  account: RsAccountOverview,
  hook: AdoptAccountsHook,
  connectionNames: Record<string, string>,
): string | null {
  // Gated on the RAW error, never on the derived refusal: a failure with no coded reason still has to
  // say something, and this mutation raises no toast.
  const error = hook.adoptAsNewErrorAccountId === account.accountId ? hook.adoptAsNewError : null;
  if (error === null || error === undefined) return null;
  const refusal = adoptRefusal(error);
  if (refusal?.reason === 'connection_exists') {
    return connectionExistsMessage(
      account,
      refusal.existingConnectionId === undefined ? undefined : connectionNames[refusal.existingConnectionId],
      refusal.existingPaused === true,
    );
  }
  return refusalMessage(error, 'That account could not be connected');
}

/**
 * Name, and ONE muted line under it — host / database — the way Settings › Accounts shows its
 * "Connected as …" line, so every row is the same height (ACT2-1031). A failed Connect takes that
 * line's place rather than adding one. Everything else lives in the View drawer, and a row's "Needs
 * attention" reason on its status badge.
 */
export function WarehouseNameCell({ row }: { row: WarehouseListRow }): React.JSX.Element {
  const { hook, connectionNames, organization } = useWarehouseListContext();
  const error =
    row.kind === 'rs_account'
      ? connectError(row.rsAccount, hook, connectionNames)
      : row.kind === 'org_account'
        ? organization.copyError(row.option.accountId)
        : null;
  return (
    <span className="flex min-w-0 flex-col">
      <TruncateWithTooltip focusable>{row.name}</TruncateWithTooltip>
      {error !== null ? (
        <span role="alert" className="flex min-w-0">
          <TruncateWithTooltip focusable className="text-xs text-destructive">
            {error}
          </TruncateWithTooltip>
        </span>
      ) : (
        row.detail !== null && (
          <TruncateWithTooltip focusable className="text-xs text-muted-foreground">
            {row.detail}
          </TruncateWithTooltip>
        )
      )}
    </span>
  );
}

/** Small brand logo + type name, as Settings › Accounts renders its Type cell. */
export function WarehouseTypeCell({ row }: { row: WarehouseListRow }): React.JSX.Element {
  const { Icon } = warehouseTypeMeta(row.type);
  return (
    <span className="flex items-center gap-2">
      {/* Decorative — the cell's own text is already the accessible name. */}
      <span aria-hidden className="flex shrink-0 items-center">
        <Icon className="size-4" />
      </span>
      <span>{row.typeLabel}</span>
    </span>
  );
}

/** One copy at a time: each copy makes RudderStack check the warehouse, which can take a minute or two. */
const ANOTHER_COPY_RUNNING = 'Wait until Lookout finishes connecting the other account.';

const STATUS_VARIANT: Record<WarehouseStatus, 'success' | 'error' | 'info' | 'outline'> = {
  connected: 'success',
  needs_attention: 'error',
  available: 'info',
  cant_connect: 'outline',
};

/**
 * What Connected means (ACT2-1128): the connection is saved and set up, not that it answered just
 * now. Lookout records no dial result, so a warehouse whose host was deleted still reads Connected.
 */
export const CONNECTED_MEANS_SAVED =
  'Saved and set up. Lookout checks the warehouse each time it runs a query. A user with the RudderStack permission to create warehouse accounts can check it now with Test connection in the row menu.';

/** What Available means for an account of another workspace (ACT2-1124). */
function organizationAccountMeans(workspaceName: string, sharedLoginWarning: string | null): string {
  return [
    `This account is in the ${workspaceName} workspace. Connect copies it into this workspace, then connects the copy.`,
    sharedLoginWarning,
  ]
    .filter((part) => part !== null)
    .join(' ');
}

/**
 * The status badge; every status but Available explains itself on hover or focus. An Available account of another
 * workspace explains itself too. A
 * connection's status reads the RudderStack account it is bound to, so until the overview answers it
 * is a placeholder, never a guess.
 */
export function WarehouseStatusBadge({ row }: { row: WarehouseListRow }): React.JSX.Element {
  const { hook } = useWarehouseListContext();
  if (row.kind === 'connection' && hook.isLoading) {
    return (
      <Skeleton className="h-5 w-20">
        <span className="sr-only">Checking status</span>
      </Skeleton>
    );
  }
  const reason =
    row.kind === 'connection' && !row.health.healthy
      ? row.health.reason
      : row.status === 'cant_connect'
        ? CANNOT_CONNECT_REASON
        : row.status === 'connected'
          ? CONNECTED_MEANS_SAVED
          : row.kind === 'org_account'
            ? organizationAccountMeans(row.option.workspaceName, row.option.sharedLoginWarning)
            : null;
  const badge = (
    <Badge variant={STATUS_VARIANT[row.status]} className="shrink-0 text-xs" tabIndex={reason === null ? undefined : 0}>
      {WAREHOUSE_STATUS_LABEL[row.status]}
    </Badge>
  );
  const status =
    reason === null ? (
      badge
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="max-w-xs">{reason}</TooltipContent>
      </Tooltip>
    );
  const orientation = row.kind === 'connection' ? row.orientation : null;
  if (orientation === null) return status;
  return (
    <span className="flex items-center gap-2">
      {status}
      <WarehouseOrientationDot indicator={orientation} />
    </span>
  );
}

/**
 * The dot's look per state. Amber while the map is being built and green once it is, the same
 * success/warning tokens the activation strip and the badges use, so both flip with the theme. A run
 * that didn't finish is an OUTLINE in the muted grey: not alarming (the connection itself works, only
 * the agent's head start is missing), and the solid-versus-ring difference carries the meaning
 * without colour, as `ActivationStrip`'s status dot does (WCAG 1.4.1).
 */
const ORIENTATION_DOT_CLASSES: Record<OrientationState, string> = {
  // The light theme's warning token is a deep amber (#c2410c, chosen for text contrast), so a solid
  // dot alone could read as an error; the pulse says "working" (and stops for reduced motion).
  orienting: 'bg-warning motion-safe:animate-pulse',
  oriented: 'bg-success',
  // ACT2-1340: a partial map is a green RING (mapped, but not all of it); a run that could not read the
  // warehouse is solid red, because the connection itself needs fixing.
  partial: 'border-2 border-success bg-transparent',
  blocked: 'bg-danger',
  not_oriented: 'border border-muted-foreground bg-transparent',
};

/**
 * ACT2-1109: a small dot beside a connection's status for its background Warehouse orientation run.
 * The tooltip says what the state means; the trigger is focusable and named, so keyboard and screen
 * reader users get the same sentence.
 */
export function WarehouseOrientationDot({ indicator }: { indicator: OrientationIndicator }): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          tabIndex={0}
          aria-label={`${indicator.label}: ${indicator.description}`}
          data-state-orientation={indicator.state}
          className="inline-flex size-4 shrink-0 items-center justify-center rounded-full outline-none focus-visible:shadow-primary"
        >
          <span aria-hidden className={cn('size-2 rounded-full', ORIENTATION_DOT_CLASSES[indicator.state])} />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <span className="font-medium">{indicator.label}.</span> {indicator.description}
      </TooltipContent>
    </Tooltip>
  );
}

/** The row's primary action — Connect or Reconnect — and, for a connection, its menu. */
export function WarehouseActionsCell({ row }: { row: WarehouseListRow }): React.JSX.Element {
  const { connectionActions } = useWarehouseListContext();
  return (
    <span className="flex items-center justify-end gap-1">
      <PrimaryAction row={row} />
      {row.kind === 'connection' && <KebabMenu label={`Actions for ${row.name}`} actions={connectionActions(row)} />}
    </span>
  );
}

function PrimaryAction({ row }: { row: WarehouseListRow }): React.JSX.Element | null {
  const { hook, organization, manageBlocked, rudderstackReadOnly, onReconnect, onConnectAccount } =
    useWarehouseListContext();
  if (row.kind === 'connection' && hook.isLoading) return null;
  if (row.kind === 'org_account') {
    const busy = organization.connectingAccountId;
    const blocked =
      row.status === 'cant_connect'
        ? CANNOT_CONNECT_REASON
        : (manageBlocked ?? (busy !== null && busy !== row.option.accountId ? ANOTHER_COPY_RUNNING : null));
    return (
      <ActionTooltip show={blocked !== null} message={blocked ?? ''}>
        <Button
          type="button"
          variant="secondary"
          label="Connect"
          aria-label={`Connect ${row.name} from ${row.option.workspaceName}`}
          Icon={<Link2 />}
          loading={busy === row.option.accountId}
          disabled={blocked !== null}
          onClick={() => {
            hook.resetAdoptAsNew();
            organization.connect(row.option);
          }}
        />
      </ActionTooltip>
    );
  }
  if (row.kind === 'rs_account') {
    const blocked = row.status === 'cant_connect' ? CANNOT_CONNECT_REASON : manageBlocked;
    return (
      <ActionTooltip show={blocked !== null} message={blocked ?? ''}>
        <Button
          type="button"
          variant="secondary"
          label="Connect"
          // Not `row.name`: two accounts on one host can share a title (see `accountActionName`).
          aria-label={`Connect ${accountActionName(row.rsAccount)}`}
          Icon={<Link2 />}
          loading={hook.adoptingNewAccountId === row.rsAccount.accountId}
          disabled={blocked !== null}
          onClick={() => {
            hook.resetAdoptAsNew();
            hook.adoptAsNewConnection({ accountId: row.rsAccount.accountId });
          }}
        />
      </ActionTooltip>
    );
  }
  if (row.health.healthy) return null;
  if (row.health.remedy === 'reconnect') {
    const blocked = rudderstackActionBlocked(manageBlocked, rudderstackReadOnly);
    return (
      <ActionTooltip show={blocked !== null} message={blocked ?? ''}>
        <Button
          type="button"
          variant="secondary"
          label="Reconnect"
          aria-label={`Reconnect ${row.name}`}
          disabled={blocked !== null}
          onClick={() => onReconnect(row.account)}
        />
      </ActionTooltip>
    );
  }
  if (row.health.remedy === 'connect_account') {
    return (
      <ActionTooltip show={manageBlocked !== null} message={manageBlocked ?? ''}>
        <Button
          type="button"
          variant="secondary"
          label="Connect"
          aria-label={`Connect ${row.name}`}
          Icon={<Link2 />}
          disabled={manageBlocked !== null}
          onClick={() => onConnectAccount(row.account)}
        />
      </ActionTooltip>
    );
  }
  return null;
}
